import React, { useState, useEffect } from 'react';
import { 
    Card, 
    CardContent, 
    CardHeader, 
    Button, 
    Typography, 
    Box, 
    Alert, 
    CircularProgress, 
    Tabs, 
    Tab, 
    Paper,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { migrateGridData, deleteOldData } from '../utils/DataMigrationTool';
import { isUserAdmin } from '../utils/adminUtils';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { materialDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { getDatabase, ref, get, set, remove, onValue, off } from 'firebase/database';

// Type definitions for react-markdown components
interface CodeProps {
    node?: any;
    inline?: boolean;
    className?: string;
    children?: React.ReactNode;
    [key: string]: any;
}

interface MigrationResult {
    success: boolean;
    migrated?: number;
    errors?: number;
    message: string;
}

interface YouTubeLink {
    id: string;
    url: string;
    title: string;
    createdAt: number;
    updatedAt: number;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`admin-tabpanel-${index}`}
            aria-labelledby={`admin-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const AdminTools: React.FC = () => {
    const [migrationResult, setMigrationResult] = useState<MigrationResult | null>(null);
    const [deletionResult, setDeletionResult] = useState<MigrationResult | null>(null);
    const [isMigrating, setIsMigrating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const [isAdmin, setIsAdmin] = useState(false);
    const [apiDocs, setApiDocs] = useState('');
    
    // YouTube Links state
    const [youtubeLinks, setYoutubeLinks] = useState<YouTubeLink[]>([]);
    const [loadingLinks, setLoadingLinks] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingLink, setEditingLink] = useState<YouTubeLink | null>(null);
    const [linkUrl, setLinkUrl] = useState('');
    const [linkTitle, setLinkTitle] = useState('');
    const [linkError, setLinkError] = useState('');
    
    // Default Token Balance state
    const [defaultTokenBalance, setDefaultTokenBalance] = useState<number | null>(null);
    const [loadingDefaultBalance, setLoadingDefaultBalance] = useState(false);
    const [savingDefaultBalance, setSavingDefaultBalance] = useState(false);
    const [defaultBalanceError, setDefaultBalanceError] = useState('');
    const [newDefaultBalance, setNewDefaultBalance] = useState('');

    useEffect(() => {
        // Check admin status
        const checkAdminStatus = async () => {
            const adminStatus = await isUserAdmin();
            setIsAdmin(adminStatus);
        };
        checkAdminStatus();

        // Fetch API documentation
        fetch('/docs/api.md')
            .then(response => response.text())
            .then(text => setApiDocs(text))
            .catch(error => console.error('Error loading API docs:', error));
    }, []);

    // Load YouTube links from Firebase
    useEffect(() => {
        if (!isAdmin) return;

        const db = getDatabase();
        const linksRef = ref(db, 'app_config/youtube_links');

        setLoadingLinks(true);
        
        const unsubscribe = onValue(linksRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const links: YouTubeLink[] = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                }));
                setYoutubeLinks(links);
            } else {
                setYoutubeLinks([]);
            }
            setLoadingLinks(false);
        }, (error) => {
            console.error('Error loading YouTube links:', error);
            setLoadingLinks(false);
        });

        return () => {
            off(linksRef);
        };
    }, [isAdmin]);

    // Load default token balance from Firebase
    useEffect(() => {
        if (!isAdmin) return;

        const db = getDatabase();
        const defaultBalanceRef = ref(db, 'app_config/defaultTokenBalance');

        setLoadingDefaultBalance(true);
        
        get(defaultBalanceRef)
            .then((snapshot) => {
                if (snapshot.exists()) {
                    const balance = snapshot.val();
                    if (typeof balance === 'number' && balance >= 0) {
                        setDefaultTokenBalance(balance);
                    } else {
                        setDefaultTokenBalance(8); // Fallback
                    }
                } else {
                    setDefaultTokenBalance(8); // Fallback if not set
                }
                setLoadingDefaultBalance(false);
            })
            .catch((error) => {
                console.error('Error loading default token balance:', error);
                setDefaultTokenBalance(8); // Fallback on error
                setLoadingDefaultBalance(false);
            });
    }, [isAdmin]);

    const handleMigrateData = async () => {
        if (isMigrating) return;

        if (!window.confirm('Are you sure you want to migrate data from "amsler_grids" to "amsler_grids_daily"?')) {
            return;
        }

        setIsMigrating(true);
        setMigrationResult(null);

        try {
            const result = await migrateGridData();
            setMigrationResult(result);
        } catch (error) {
            setMigrationResult({
                success: false,
                message: `Error: ${error instanceof Error ? error.message : String(error)}`
            });
        } finally {
            setIsMigrating(false);
        }
    };

    const handleDeleteOldData = async () => {
        if (isDeleting) return;

        if (!window.confirm('Are you sure you want to delete data from the old "amsler_grids" path? This cannot be undone!')) {
            return;
        }

        setIsDeleting(true);
        setDeletionResult(null);

        try {
            const result = await deleteOldData();
            setDeletionResult(result);
        } catch (error) {
            setDeletionResult({
                success: false,
                message: `Error: ${error instanceof Error ? error.message : String(error)}`
            });
        } finally {
            setIsDeleting(false);
        }
    };

    // YouTube Links handlers
    const handleOpenEditDialog = (link?: YouTubeLink) => {
        if (link) {
            setEditingLink(link);
            setLinkUrl(link.url);
            setLinkTitle(link.title);
        } else {
            setEditingLink(null);
            setLinkUrl('');
            setLinkTitle('');
        }
        setLinkError('');
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingLink(null);
        setLinkUrl('');
        setLinkTitle('');
        setLinkError('');
    };

    const validateYouTubeUrl = (url: string): boolean => {
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
        return youtubeRegex.test(url);
    };

    const handleSaveLink = async () => {
        setLinkError('');

        if (!linkUrl.trim()) {
            setLinkError('URL is required');
            return;
        }

        if (!linkTitle.trim()) {
            setLinkError('Title is required');
            return;
        }

        if (!validateYouTubeUrl(linkUrl)) {
            setLinkError('Please enter a valid YouTube URL');
            return;
        }

        try {
            const db = getDatabase();
            const now = Date.now();

            if (editingLink) {
                // Update existing link
                const linkRef = ref(db, `app_config/youtube_links/${editingLink.id}`);
                await set(linkRef, {
                    url: linkUrl.trim(),
                    title: linkTitle.trim(),
                    createdAt: editingLink.createdAt,
                    updatedAt: now
                });
            } else {
                // Create new link
                const linksRef = ref(db, 'app_config/youtube_links');
                const newLinkRef = ref(db, 'app_config/youtube_links');
                const newLinkId = `link_${now}`;
                await set(ref(db, `app_config/youtube_links/${newLinkId}`), {
                    url: linkUrl.trim(),
                    title: linkTitle.trim(),
                    createdAt: now,
                    updatedAt: now
                });
            }

            handleCloseEditDialog();
        } catch (error) {
            console.error('Error saving YouTube link:', error);
            setLinkError('Failed to save link. Please try again.');
        }
    };

    const handleDeleteLink = async (linkId: string) => {
        if (!window.confirm('Are you sure you want to delete this YouTube link?')) {
            return;
        }

        try {
            const db = getDatabase();
            const linkRef = ref(db, `app_config/youtube_links/${linkId}`);
            await remove(linkRef);
        } catch (error) {
            console.error('Error deleting YouTube link:', error);
            alert('Failed to delete link. Please try again.');
        }
    };

    // Default Token Balance handlers
    const handleSaveDefaultBalance = async () => {
        setDefaultBalanceError('');

        if (!newDefaultBalance.trim()) {
            setDefaultBalanceError('Please enter a default token balance');
            return;
        }

        const balance = parseInt(newDefaultBalance, 10);
        if (isNaN(balance) || balance < 0) {
            setDefaultBalanceError('Please enter a valid number (0 or greater)');
            return;
        }

        setSavingDefaultBalance(true);

        try {
            const db = getDatabase();
            const defaultBalanceRef = ref(db, 'app_config/defaultTokenBalance');
            await set(defaultBalanceRef, balance);
            
            setDefaultTokenBalance(balance);
            setNewDefaultBalance('');
            alert('Default token balance updated successfully!');
        } catch (error) {
            console.error('Error saving default token balance:', error);
            setDefaultBalanceError('Failed to save default balance. Please try again.');
        } finally {
            setSavingDefaultBalance(false);
        }
    };

    // Custom renderer for code blocks
    const renderers = {
        code: ({ node, inline, className, children, ...props }: CodeProps) => {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
                <SyntaxHighlighter
                    style={materialDark}
                    language={match[1]}
                    PreTag="div"
                    {...props}
                >
                    {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
            ) : (
                <code className={className} {...props}>
                    {children}
                </code>
            );
        }
    };

    if (!isAdmin) {
        return (
            <Card sx={{ m: 2 }}>
                <CardContent>
                    <Alert severity="error">
                        You do not have access to the admin tools.
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{ m: 2 }}>
            <CardHeader title="Admin Tools" />
            <CardContent>
                <Tabs
                    value={selectedTab}
                    onChange={(_, newValue) => setSelectedTab(newValue)}
                    aria-label="admin tools tabs"
                >
                    <Tab label="Data Migration" />
                    <Tab label="YouTube Links" />
                    <Tab label="Token Balance" />
                    <Tab label="API Documentation" />
                </Tabs>

                <TabPanel value={selectedTab} index={0}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Database Migration Utilities</Typography>
                    
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            Migrate data from "amsler_grids" to "amsler_grids_daily"
                        </Typography>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleMigrateData}
                            disabled={isMigrating}
                            startIcon={isMigrating ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {isMigrating ? 'Migrating...' : 'Run Migration'}
                        </Button>
                        
                        {migrationResult && (
                            <Alert 
                                severity={migrationResult.success ? 'success' : 'error'} 
                                sx={{ mt: 1 }}
                            >
                                {migrationResult.message}
                                {migrationResult.migrated !== undefined && (
                                    <Typography variant="body2">
                                        Records migrated: {migrationResult.migrated}
                                    </Typography>
                                )}
                                {migrationResult.errors !== undefined && migrationResult.errors > 0 && (
                                    <Typography variant="body2">
                                        Errors: {migrationResult.errors}
                                    </Typography>
                                )}
                            </Alert>
                        )}
                    </Box>
                    
                    <Box>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            Delete data from old "amsler_grids" path (only after successful migration)
                        </Typography>
                        <Button 
                            variant="contained" 
                            color="error" 
                            onClick={handleDeleteOldData}
                            disabled={isDeleting || (migrationResult?.success !== true && migrationResult?.migrated !== 0)}
                            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {isDeleting ? 'Deleting...' : 'Delete Old Data'}
                        </Button>
                        
                        {deletionResult && (
                            <Alert 
                                severity={deletionResult.success ? 'success' : 'error'} 
                                sx={{ mt: 1 }}
                            >
                                {deletionResult.message}
                            </Alert>
                        )}
                    </Box>
                </TabPanel>

                <TabPanel value={selectedTab} index={1}>
                    <Typography variant="h6" sx={{ mb: 2 }}>YouTube Tutorial Links Management</Typography>
                    
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Manage YouTube tutorial links that appear in the mobile app instructions screen.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenEditDialog()}
                        >
                            Add Link
                        </Button>
                    </Box>

                    {loadingLinks ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : youtubeLinks.length === 0 ? (
                        <Alert severity="info">
                            No YouTube links configured. Click "Add Link" to add one.
                        </Alert>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Title</TableCell>
                                        <TableCell>URL</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {youtubeLinks.map((link) => (
                                        <TableRow key={link.id}>
                                            <TableCell>{link.title}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            maxWidth: 300, 
                                                            overflow: 'hidden', 
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {link.url}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => window.open(link.url, '_blank')}
                                                    >
                                                        <OpenInNewIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenEditDialog(link)}
                                                    color="primary"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteLink(link.id)}
                                                    color="error"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Edit/Create Dialog */}
                    <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
                        <DialogTitle>
                            {editingLink ? 'Edit YouTube Link' : 'Add YouTube Link'}
                        </DialogTitle>
                        <DialogContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                                <TextField
                                    label="Title"
                                    value={linkTitle}
                                    onChange={(e) => setLinkTitle(e.target.value)}
                                    placeholder="Youtube Tutorial Videos"
                                    fullWidth
                                    required
                                />
                                <TextField
                                    label="YouTube URL"
                                    value={linkUrl}
                                    onChange={(e) => setLinkUrl(e.target.value)}
                                    placeholder="https://www.youtube.com/watch?v=..."
                                    fullWidth
                                    required
                                    helperText="Enter a valid YouTube URL (youtube.com or youtu.be)"
                                />
                                {linkError && (
                                    <Alert severity="error">{linkError}</Alert>
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseEditDialog}>Cancel</Button>
                            <Button onClick={handleSaveLink} variant="contained" color="primary">
                                {editingLink ? 'Update' : 'Add'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </TabPanel>

                <TabPanel value={selectedTab} index={2}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Default Token Balance Management</Typography>
                    
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Set the default token balance for new users. This value is used when:
                        </Typography>
                        <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                            <li>User has no balance in local storage</li>
                            <li>User has no balance in Firebase tokenIds path</li>
                            <li>New user first launches the app</li>
                        </Box>
                        
                        {loadingDefaultBalance ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Current Default Token Balance
                                    </Typography>
                                    <Typography variant="h4" color="primary">
                                        {defaultTokenBalance ?? 8}
                                    </Typography>
                                </Box>
                                
                                <TextField
                                    label="New Default Token Balance"
                                    type="number"
                                    value={newDefaultBalance}
                                    onChange={(e) => {
                                        setNewDefaultBalance(e.target.value);
                                        setDefaultBalanceError('');
                                    }}
                                    inputProps={{ min: 0, step: 1 }}
                                    fullWidth
                                    helperText="Enter the number of tokens new users should receive"
                                    error={!!defaultBalanceError}
                                />
                                
                                {defaultBalanceError && (
                                    <Alert severity="error">{defaultBalanceError}</Alert>
                                )}
                                
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSaveDefaultBalance}
                                    disabled={savingDefaultBalance || newDefaultBalance === (defaultTokenBalance?.toString() ?? '8')}
                                    startIcon={savingDefaultBalance ? <CircularProgress size={20} color="inherit" /> : null}
                                >
                                    {savingDefaultBalance ? 'Saving...' : 'Update Default Balance'}
                                </Button>
                                
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Note:</strong> This change will affect all new users going forward. 
                                        Existing users' balances will not be changed.
                                    </Typography>
                                </Alert>
                            </Box>
                        )}
                    </Box>
                </TabPanel>

                <TabPanel value={selectedTab} index={3}>
                    <Paper 
                        sx={{ 
                            p: 2, 
                            maxHeight: 'calc(100vh - 300px)', 
                            overflow: 'auto' 
                        }}
                    >
                        <ReactMarkdown
                            components={renderers}
                        >
                            {apiDocs}
                        </ReactMarkdown>
                    </Paper>
                </TabPanel>
            </CardContent>
        </Card>
    );
};

export default AdminTools; 